import Product, { ProductType } from "@models/product";
import React, { useState, useRef, useEffect } from "react";
import styles from "./ProductCard.module.scss";
import { useNavigate } from "react-router";
import { cartStore, authStore } from "@stores/main";
import { generateProductViewUrl } from "@utils/urlUtils";
import { useBreadcrumbNavigation } from "@utils/breadcrumbNavigation";
import { formatDiscountPercentage } from "@utils/formatDiscountPercentage";
import { gtagService } from "../../services/gtag";
import { pageTrackingService } from "../../services/pageTracking";

const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
  const navigate = useNavigate();
  const { goToLogin } = useBreadcrumbNavigation();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const addToCartTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (addToCartTimeoutRef.current) {
        clearTimeout(addToCartTimeoutRef.current);
      }
    };
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on cart icon or if adding to cart
    if (isAddingToCart || cartStore.loading) {
      e.preventDefault();
      return;
    }
    navigate(generateProductViewUrl(product));
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Prevent multiple clicks
    if (isAddingToCart || cartStore.loading) {
      return;
    }

    if (!authStore.isAuthenticated) {
      goToLogin();
      return;
    }

    // Clear any existing timeout
    if (addToCartTimeoutRef.current) {
      clearTimeout(addToCartTimeoutRef.current);
    }

    setIsAddingToCart(true);
    try {
      const res = await cartStore.addItemToCart(product.id);
      if (!res) {
        return;
      }

      pageTrackingService.trackProductAddToCart(product.id, product.title, 'product_card');

      // Track add to cart conversion
      gtagService.trackAddToCart({
        currency: product.currency || 'USD',
        value: product.buyPrice,
        items: [{
          item_id: product.id,
          item_name: product.title,
          price: product.buyPrice,
          quantity: 1
        }]
      });
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      // Add a small delay before allowing new interactions to prevent rapid clicks
      addToCartTimeoutRef.current = setTimeout(() => {
        setIsAddingToCart(false);
      }, 300);
    }
  };

  return (
    <div
      className={
        styles["game"] + " " + (product.isSoldOut ? styles["out-of-stock"] : "")
      }
      key={product.id}
      onClick={handleClick}
      style={{ cursor: 'pointer' }}
      role="button"
      tabIndex={0}
    >
      <div 
        className={styles["game-image"]} 
      >
        {!!product.discountPercentage && (
          <>
            <div className={`${styles["discount-tag"]} ${product.discountPercentage > 99 ? styles["discount-tag-large"] : ''}`}>
              <span className={`${product.hotOffer ? styles["discount-hotoffer"] : styles["discount-offered"]} is-flex is-align-items-start is-flex-direction-column fs-20`}>
                {formatDiscountPercentage(product.discountPercentage)}%
                <span className={styles["discount-off"] + " fs-14 fw-700"}>OFF</span>
              </span>
            </div>
            {product.hotOffer && (
              <div className={styles["limited-offer-tag"] + " position-absolute"}>
                <img
                  src={
                    process.env.PUBLIC_URL +
                    "/images/tags/limited-offer-tag.svg"
                  }
                  alt="Limited Offer"
                />
              </div>
            )}
          </>
        )}

        {/* {installIcons && (
          <div className="install-icons">
            {installIcons.map((icon) => (
              <img
                key={icon}
                src={`/images/icons/${icon}-icon.svg`}
                alt={`${icon} icon`}
              />
            ))}
          </div>
        )} */}

        <img 
          src={product.poster || `${process.env.PUBLIC_URL}/images/store-desktop-thumbnail.png`} 
          alt={product.title} 
          onError={(e) => {
            const target = e.currentTarget as HTMLImageElement;
            target.onerror = null; // Prevent infinite loop if fallback also fails
            target.src = `${process.env.PUBLIC_URL}/images/store-desktop-thumbnail.png`;
          }}
        />

        {product.genres?.length > 0 && (
          <div className={styles["all-tags"]}>
            {product.genres.includes("free") && (
              <img
                className={styles["free-on-tag"] + " position-absolute"}
                src={process.env.PUBLIC_URL + "/images/tags/free-on.svg"}
                alt="Free"
              />
            )}
            {product.genres.includes("updating") && (
              <img
                className={styles["game-tags"] + " position-absolute"}
                src={process.env.PUBLIC_URL + "/images/tags/updating.svg"}
                alt="Updating"
              />
            )}
          </div>
        )}

        {product.isSoldOut && (
          <div className={styles["stock-tag"] + " position-absolute fs-14 fw-600 color-lightgray"}>OUT OF STOCK</div>
        )}
      </div>

      <div className={styles["game-title"] + " fs-16 color-lightgray"}>{product.title}</div>
      <div className={styles["game-region"] + " fs-14 f-gray fw-400"}>{product.availableRegion}</div>
      <div className={styles["price-section"]}>
        <div className={styles["price-row"]}>
          <div className={styles["price-content"]}>
            <div className={styles["price-info"]}>
              {product.type === ProductType.Wallet && (
                <div className={styles["from-text"] + " fs-11 fw-500 f-gray"}>From</div>
              )}
              <div className={styles["game-price"] + " fs-20 fw-500 color-lightgray"}>{product.buyPriceString}</div>
              {product.displayPrice > product.buyPrice && (
                <div className={styles["strike-price"] + " fs-14 fw-500 f-gray"}>
                  {product.displayPriceString}
                </div>
              )}
            </div>
          </div>
          {/* Add to Cart Icon - always visible */}
          {!product.isSoldOut && (
            cartStore.items.some((item) => item.id === product.id) || isAddingToCart ? (
              <span
                className={styles.addCartIcon}
                aria-disabled="true"
                tabIndex={-1}
                style={{
                  cursor: "default"
                }}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <img
                  src={process.env.PUBLIC_URL + "/images/icons/added-to-cart.svg"}
                  alt="Added to Cart"
                  width={24}
                  height={24}
                />
              </span>
            ) : (
              <span
                className={styles.addCartIcon}
                onClick={handleAddToCart}
                style={{
                  cursor: "pointer"
                }}
                aria-disabled={false}
              >
                <img
                  src={process.env.PUBLIC_URL + "/images/icons/add-to-cart.svg"}
                  alt="Add to Cart"
                  width={24}
                  height={24}
                />
              </span>
            )
          )}
        </div>
      </div>
      {
        product.availableQuantity <= 10 && product.availableQuantity != 0 &&
        <div className={styles["stock-left"]}>
          <div className={styles["stock-left-count"] + " fs-16 fw-500"}>
            Only {product.availableQuantity} in stock
          </div>
        </div>
      }
    </div>
  );
};

export default ProductCard;
